<template>
    <Modal
        :show="true"
        :title="isNew ? $t('dashboard.addWidget') : $t('dashboard.editWidget')"
        :esc-handler="escHandler"
        :enter-handler="enterHandler"
        style-class="modal-widget"
        class="for-unknown-height"
        @close="$emit('cancel')"
    >
        <form>
            <div class="wrapper">
                <span v-if="$v.configuration.name.$invalid" class="tooltiptext">
                    {{ errorDisplay($v.configuration.name, { val: MAX_NAME_LENGTH }) }}
                </span>
                <div :class="['input-with-insidelabel', { tooltiperror: $v.configuration.name.$invalid }]">
                    <input
                        id="listenIn"
                        ref="widgetName"
                        v-model="configuration.name"
                        v-focus-inserted
                        type="text"
                    />
                    <label for="listenIn">{{ $t('common.name') }}</label>
                </div>
            </div>
            <div class="wrapper">
                <SelectBox
                    id="forAT_AddWidget-type"
                    :placeholder="$t('dashboard.widgetType')"
                    :value="type"
                    :is-disabled="!isNew"
                    :options="widgetTypesList"
                    @input="setWidgetType($event)"
                />
            </div>
            <div v-if="userType === USER_TYPES.SYS_ADMIN" class="multiselect-wrapper">
                <Multiselect
                    :placeholder="
                        !!configuration.aa && configuration.aa.length > 0
                            ? $t('reporting.commonLabels.aAreas')
                            : $t('reporting.commonLabels.allAA')
                    "
                    :multiple="true"
                    :close-on-select="false"
                    :options="allAssignmentAreas"
                    label="name"
                    track-by="name"
                    :value="configuredAssignmentAreas"
                    :select-label="''"
                    :deselect-label="''"
                    :selected-label="''"
                    :limit="5"
                    @input="updateAreasAndLocations"
                >
                    <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
                </Multiselect>
            </div>
            <Component
                :is="widgetFormComponent"
                v-if="widgetFormComponent !== ''"
                :value="configuration"
                :locations="locations"
                @input="$emit('input', $event)"
            />
        </form>
        <div slot="footer">
            <button class="btn btn-ok" :disabled="isOkDisabled" @click="$emit('done')">
                {{ $t('common.save') }}
            </button>
            <button class="btn btn-cancel" @click="$emit('cancel')">{{ $t('common.cancel') }}</button>
        </div>
    </Modal>
</template>

<script type="text/babel">
import Modal from './../common/Modal';
import SelectBox from './../common/SelectBox';
import Multiselect from 'moxa-vue-multiselect';
import SystemActivityForm from './widget-configuration-forms/SystemActivityForm';
import TargetTimeForm from './widget-configuration-forms/TargetTimeForm';
import StaffPerformanceForm from './widget-configuration-forms/StaffPerformanceForm';
import NursePresenceForm from './widget-configuration-forms/NursePresenceForm';
import RecentEventsForm from './widget-configuration-forms/RecentEventsForm';
import ExceptionTimeForm from './widget-configuration-forms/ExceptionTimeForm';
import AverageTimeForm from './widget-configuration-forms/AverageTimeForm';
import { minimumLength, maximumNameLength, errorDisplay } from '../../validators';
import { MAX_NAME_LENGTH, WIDGET_TYPES, USER_TYPES } from '../../constants';
import flatten from 'lodash-es/flatten';
import map from 'lodash-es/map';
import { transformArrayToOptions } from './widgetHelpers';
import { mapGetters, mapState } from 'vuex';
import commonDirectives from '../common/commonDirectives';
import { sortLocations } from '../common/helpers';

export default {
    components: {
        Modal,
        Multiselect,
        SelectBox,
        SystemActivityForm,
        StaffPerformanceForm,
        TargetTimeForm,
        NursePresenceForm,
        RecentEventsForm,
        ExceptionTimeForm,
        AverageTimeForm,
    },

    mixins: [commonDirectives],

    model: {
        prop: 'configuration',
    },

    constants: {
        MAX_NAME_LENGTH,
        USER_TYPES,
    },

    props: {
        configuration: {
            type: Object,
            required: true,
        },

        type: {
            type: Number,
            required: true,
        },

        isAssignmentsWidgetExist: {
            type: Boolean,
            required: true,
        },
    },

    data() {
        return {
            isNew: !this.type,
            isChanged: false,
        };
    },

    computed: {
        ...mapGetters({ userType: 'user/userType' }),
        ...mapState({ allAssignmentAreas: 'assignmentArea' }),

        isOkDisabled() {
            return !this.isNew
                ? !this.isChanged || this.$v.configuration.$invalid
                : this.$v.configuration.$invalid;
        },

        /**
         * Returns array of assignment area options for multi-select value
         * (present in current widget configuration)
         * @return {{name: string, locations: {name: string}[]}[]} array of full assignment area objects
         */
        configuredAssignmentAreas() {
            let configuredAreas = this.configuration.aa || [];
            let sortedAareas = sortLocations(this.allAssignmentAreas);
            return sortedAareas.filter(area => configuredAreas.includes(area.name));
        },

        /**
         * Returns array of selected assignment area options from multi-select
         * @return {{name: string, locations: {name: string}[]}[]} array of full assignment area objects
         */
        selectedAA() {
            return (this.configuration.aa || []).length === 0
                ? this.allAssignmentAreas
                : this.configuredAssignmentAreas;
        },

        /**
         * Returns list of all locations in selected assignment area(s)
         * as a options for Multiselect component
         * @return {{ name: string, value: string}[]}
         */
        locations() {
            let locations = sortLocations(map(flatten(map(this.selectedAA, aa => aa.locations)), 'name'));

            return transformArrayToOptions(locations);
        },

        /**
         * Returns possible types of widgets (options list) for logged user
         * @return {{ value: number, name: string }[]}
         */
        widgetTypesList() {
            let widgetTypes = [];
            switch (this.userType) {
                case USER_TYPES.SYS_ADMIN:
                    widgetTypes = [1, 2, 3, 4, 5, 6, 7];
                    break;
                case USER_TYPES.USER_ADMIN:
                case USER_TYPES.SUPER_USER:
                    if (this.isAssignmentsWidgetExist && this.isNew) {
                        widgetTypes = [1, 2, 3, 4, 5, 6, 7];
                    } else {
                        widgetTypes = [1, 2, 3, 4, 5, 6, 7, 8];
                    }
                    break;
                case USER_TYPES.USER:
                    if (this.isAssignmentsWidgetExist) {
                        widgetTypes = [2, 5, 6];
                    } else {
                        widgetTypes = [2, 5, 6, 8];
                    }
            }

            return widgetTypes.map(value => ({
                value,
                name: this.$t('dashboard.widgetType' + value + '.name'),
            }));
        },

        /**
         * Returns name of form component for currently selected widget type
         * @return {string}
         */
        widgetFormComponent() {
            let type = this.type;

            let widgetNames = Object.keys(WIDGET_TYPES);
            let widgetKeys = Object.values(WIDGET_TYPES);

            if (widgetKeys.includes(type) && type !== 8) {
                let widgetName = widgetNames[widgetKeys.indexOf(type)];
                return `${widgetName}Form`;
            }

            return '';
        },
    },

    watch: {
        configuration: {
            handler() {
                if (!this.isNew && !this.isChanged) {
                    this.isChanged = true;
                }
            },
            deep: true,
        },

        type(newVal) {
            if (!newVal) {
                this.isNew = true;
            }
        },
    },

    validations() {
        return {
            configuration: { name: { minimumLength, maximumNameLength } },
        };
    },

    created() {
        if (this.isNew) {
            this.setWidgetType(this.userType === USER_TYPES.USER ? 2 : 1);
        }
        this.$nextTick(() => {
            this.$refs.widgetName.select();
        });
    },

    methods: {
        escHandler() {
            this.$emit('cancel');
        },

        enterHandler() {
            if (!this.isOkDisabled) {
                this.$emit('done');
            }
        },

        errorDisplay,

        /**
         * Sets selected assignment areas, updates array of selected locations according to selected assignment areas,
         */
        updateAreasAndLocations(aa) {
            let assignmentAreas = !!aa && aa.length > 0 ? aa : this.allAssignmentAreas;
            let allAvailableLocations = map(flatten(map(assignmentAreas, 'locations')), 'name');

            let locations = (this.configuration.locations || []).filter(location =>
                allAvailableLocations.includes(location)
            );

            this.$emit('input', {
                ...this.configuration,
                aa: (aa || []).map(area => area.name),
                locations,
            });
        },

        /**
         * Emits event to set widget type and new widget initial configuration
         * @param {number} type
         */
        setWidgetType(type) {
            let configuration = {
                name: this.$t('dashboard.widgetType' + type + '.name') + ' Widget',
                aa: [],
                locations: [],
            };

            switch (type) {
                case 1:
                    configuration.period = 1;
                    break;
                case 2:
                    configuration.period = 30;
                    configuration.call_types = [];
                    break;
                case 3:
                    configuration.period = 1;
                    break;
                case 4:
                    configuration.period = 1;
                    configuration.nurse_call = 1;
                    break;
                case 5:
                    configuration.call_types = [];
                    configuration.duration = 0;
                    break;
                case 6:
                    break;
                case 7:
                    configuration.call_type = 1;
                    configuration.processing_time = 1;
                    break;
                case 8:
                    delete configuration.aa;
                    delete configuration.locations;
                    break;
                default:
                    configuration = {};
            }

            this.$emit('input', configuration);
            this.$emit('update:type', type);
        },
    },
};
</script>
